import { isValidElement, memo, useRef, useState, type ReactNode } from 'react';
import Markdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import { useI18n } from '../i18n/context.js';
import { copyToClipboard } from '../lib/dom.js';
import { hapticFeedback } from '../lib/haptics.js';
import { Icon } from './Icon.js';

function nodeText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return nodeText(node.props.children);
  return '';
}

function CodeBlock({ children }: { children?: ReactNode }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const code = isValidElement<{ className?: string; children?: ReactNode }>(children) ? children : null;
  const language = /language-([\w+#-]+)/.exec(code?.props.className ?? '')?.[1] ?? '';
  const text = nodeText(children).replace(/\n$/, '');

  async function copy() {
    if (!(await copyToClipboard(text))) return;
    hapticFeedback('select');
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="code-block">
      <div className="code-block-header">
        <span className="code-block-lang">{language}</span>
        <button type="button" className="code-block-copy" onClick={() => void copy()}>
          <Icon name={copied ? 'check' : 'copy'} size={14} />
          {copied ? t('message.copied') : t('message.copy')}
        </button>
      </div>
      <pre>{children}</pre>
    </div>
  );
}

const remarkPlugins = [remarkGfm];
// Language auto-detection runs highlight.js over every grammar for every
// unlabelled block on every keystroke of a streamed answer; fenced blocks
// almost always name their language, so only those are highlighted.
const rehypePlugins = [[rehypeHighlight, { detect: false, ignoreMissing: true }]] as const;

/** Markdown for assistant answers: GFM, syntax-highlighted code with a copy button. */
export const MarkdownContent = memo(function MarkdownContent({ content }: { content: string }) {
  return (
    <div className="markdown">
      <Markdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins as unknown as Parameters<typeof Markdown>[0]['rehypePlugins']}
        components={{
          pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
          a: ({ children, href }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
          ),
        }}
      >
        {content}
      </Markdown>
    </div>
  );
});
